/**
 * The conversational partner (D235) — who "her" means while you are talking
 * with her.
 *
 * @remarks
 * Recency (D67) answers what was last spoken OF; it cannot answer who the
 * player is talking TO. After the occupant asks "Is she with you?" and
 * EURYDICE adds a line, recency hands `her` to EURYDICE, and `talk to her`
 * reaches the wrong voice. The rule people actually talk by is simpler: the
 * body you addressed last, or that last addressed you, is who its own
 * pronoun names — until you walk out of its earshot or address somebody
 * else.
 *
 * The partner is session state (`session.conversationPartnerId`): set when
 * the player addresses an actor (`talk`, `ask`, `give`, `show`, or an answer
 * to its dialogue question) and when an actor asks the player a prompt
 * (`ask_prompt`, `opensWith`); never cleared, because a partner out of
 * earshot simply stops applying, and the next body addressed replaces it.
 * What the parser receives is the read half, `entities/partner-pronoun.ts`:
 * the antecedent map — the partner's declared `pronoun`, in its object form,
 * naming the partner's ref — and a catalog in which that word is the
 * partner's alone, both only while the partner is still addressable from
 * where the player stands. An actor that declares no pronoun contributes
 * nothing, and recency stands.
 *
 * @packageDocumentation
 */

import type { ActorId, RuntimeState } from '@orpheus/content-schema';

import type { RuntimeAction } from '../actions/runtime-action.js';

export { pronounAntecedentsOf } from '../entities/partner-pronoun.js';

/**
 * The actor a narrowed action ADDRESSES — the body spoken to, or offered a
 * thing. A verb merely aimed at a body (`attack`, `take`) addresses nobody.
 *
 * @param action - The narrowed action
 * @returns The addressed actor, or `undefined` when the action addresses none
 */
export const addressedActorOf = (action: RuntimeAction): ActorId | undefined => {
	if (action.type === 'ask') {
		return action.addressee?.kind === 'actor' ? action.addressee.actorId : undefined;
	}

	return action.type === 'give' || action.type === 'show' ? action.actorId : undefined;
};

/**
 * Remember who the player is talking with.
 *
 * @param state - The state to write into
 * @param actorId - The partner, or `undefined` to leave the record as it is
 * @returns The state with the partner recorded (the same state when nothing changes)
 */
export const rememberConversationPartner = (state: RuntimeState, actorId: ActorId | undefined): RuntimeState =>
	actorId === undefined || state.session.conversationPartnerId === actorId
		? state
		: { ...state, session: { ...state.session, conversationPartnerId: actorId }};
